import type { World } from '@invaders/sim';
import { FP, UNIT_W, UNIT_H, type Unit } from '@invaders/game';
import { TEAM_COLORS } from './teams';
import type { Camera } from './camera';
import { buildPalette, rgbToAbgr } from './palette';
import { themedColor, type Theme } from './themes';

/** GDD: refreshed twice a second. */
const REFRESH_MS = 500;

/** The whole island, small and semi-transparent, with the camera frame and the units. A click moves the camera there. */
export class Minimap {
  readonly canvas = document.createElement('canvas');
  private readonly ctx = this.canvas.getContext('2d')!;
  private readonly image: ImageData;
  private readonly pixels: Uint32Array;
  private palette = buildPalette();
  private lastRefresh = -Infinity;

  constructor(private readonly mapW: number, private readonly mapH: number, private readonly step = 4) {
    this.canvas.width = Math.ceil(mapW / step);
    this.canvas.height = Math.ceil(mapH / step);
    this.canvas.className = 'minimap';
    this.image = this.ctx.createImageData(this.canvas.width, this.canvas.height);
    this.pixels = new Uint32Array(this.image.data.buffer);
  }

  /** The map's colours (the sky keeps the empty cell's dark colour: the minimap is semi-transparent). */
  setTheme(theme: Theme): void {
    this.palette = this.palette.map((c, id) => (id === 0 ? c : rgbToAbgr(themedColor(theme, id))));
    this.lastRefresh = -Infinity;
  }

  draw(world: World, cam: Camera, now: number, units: readonly Unit[] = []): void {
    if (now - this.lastRefresh >= REFRESH_MS) {
      this.lastRefresh = now;
      const { w, data } = world.physique;
      const s = this.step;
      const mw = this.canvas.width;
      for (let y = 0; y < this.canvas.height; y++) {
        for (let x = 0; x < mw; x++) this.pixels[y * mw + x] = this.palette[data[y * s * w + x * s]];
      }
    }
    this.ctx.putImageData(this.image, 0, 0);
    this.ctx.strokeStyle = '#fff';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(cam.x / this.step + 0.5, cam.y / this.step + 0.5, cam.spanW / this.step, cam.spanH / this.step);
    for (const u of units) {
      if (!u.alive) continue;
      this.ctx.fillStyle = TEAM_COLORS[u.player % TEAM_COLORS.length];
      this.ctx.fillRect(Math.floor((u.x / FP + UNIT_W / 2) / this.step) - 2, Math.floor((u.y / FP + UNIT_H / 2) / this.step) - 2, 4, 4);
    }
  }

  /** Map cell under a click on the minimap. */
  cellAt(e: MouseEvent): [number, number] {
    const r = this.canvas.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * this.mapW, ((e.clientY - r.top) / r.height) * this.mapH];
  }
}
